import React, { useState, useEffect } from 'react';
import {
  FileText,
  Plus,
  Filter,
  Download,
  Trash2,
  Edit,
  Eye,
  Calendar,
  BarChart3,
  PieChart,
  LineChart,
  TrendingUp,
  Users,
  Monitor,
  Server,
  Smartphone,
  Tablet,
  Apple,
  X,
  Save,
  CheckCircle,
  XCircle,
  Clock
} from 'lucide-react';

interface Report {
  id: string;
  name: string;
  description?: string;
  chartType: 'bar' | 'pie' | 'line' | 'table';
  dataSource: 'devices' | 'compliance' | 'applications' | 'deployments' | 'policies';
  filters: ReportFilter[];
  columns: string[];
  createdAt: string;
  lastRun?: string;
  createdBy: string;
}

interface ReportFilter {
  field: string;
  operator: 'equals' | 'contains' | 'greater_than' | 'less_than' | 'between' | 'in';
  value: string | string[];
}

const CustomReports = () => {
  const [reports, setReports] = useState<Report[]>([]);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedReport, setSelectedReport] = useState<Report | null>(null);
  const [viewingReport, setViewingReport] = useState<Report | null>(null);
  const [loading, setLoading] = useState(false);

  // Form state for creating/editing reports
  const [formData, setFormData] = useState<Partial<Report>>({
    name: '',
    description: '',
    chartType: 'bar',
    dataSource: 'devices',
    filters: [],
    columns: []
  });

  // Available fields by data source
  const dataSourceFields: { [key: string]: string[] } = {
    devices: ['name', 'os', 'platform', 'status', 'last_seen', 'compliance_status', 'department'],
    compliance: ['device_id', 'policy_id', 'compliance_status', 'checked_at', 'violations'],
    applications: ['name', 'platform', 'version', 'status', 'deployment_method', 'uploaded_at'],
    deployments: ['application_id', 'target_type', 'status', 'progress', 'started_at', 'completed_at'],
    policies: ['name', 'platform', 'type', 'status', 'created_at', 'updated_at']
  };

  // Load reports from localStorage (in production, fetch from API)
  useEffect(() => {
    const savedReports = localStorage.getItem('buntu_custom_reports');
    if (savedReports) {
      try {
        setReports(JSON.parse(savedReports));
      } catch (e) {
        console.error('Failed to load reports:', e);
      }
    }
  }, []);

  const saveReports = (updatedReports: Report[]) => {
    localStorage.setItem('buntu_custom_reports', JSON.stringify(updatedReports));
    setReports(updatedReports);
  };

  const handleCreateReport = () => {
    setFormData({
      name: '',
      description: '',
      chartType: 'bar',
      dataSource: 'devices',
      filters: [],
      columns: []
    });
    setShowCreateModal(true);
  };

  const handleEditReport = (report: Report) => {
    setFormData(report);
    setSelectedReport(report);
    setShowEditModal(true);
  };

  const handleSaveReport = () => {
    if (!formData.name?.trim()) {
      window.alert('Please enter a report name');
      return;
    }

    const newReport: Report = {
      id: selectedReport?.id || `report-${Date.now()}`,
      name: formData.name,
      description: formData.description,
      chartType: formData.chartType || 'bar',
      dataSource: formData.dataSource || 'devices',
      filters: formData.filters || [],
      columns: formData.columns || [],
      createdAt: selectedReport?.createdAt || new Date().toISOString(),
      createdBy: 'admin@datail.in'
    };

    if (selectedReport) {
      // Update existing report
      const updated = reports.map(r => r.id === selectedReport.id ? newReport : r);
      saveReports(updated);
      setShowEditModal(false);
    } else {
      // Create new report
      saveReports([...reports, newReport]);
      setShowCreateModal(false);
    }
    setSelectedReport(null);
    setFormData({});
  };

  const handleDeleteReport = (reportId: string) => {
    if (window.confirm('Are you sure you want to delete this report?')) {
      const updated = reports.filter(r => r.id !== reportId);
      saveReports(updated);
    }
  };

  const handleRunReport = (report: Report) => {
    setViewingReport(report);
    // In production, this would fetch actual data from backend
  };

  const handleExportReport = (report: Report, format: 'csv' | 'pdf' | 'excel') => {
    // In production, this would call backend API to generate export
    console.log(`Exporting report ${report.id} as ${format}`);
    window.alert(`Exporting report "${report.name}" as ${format.toUpperCase()}...`);
  };

  const addFilter = () => {
    const fields = dataSourceFields[formData.dataSource || 'devices'] || [];
    if (fields.length === 0) return;
    
    setFormData({
      ...formData,
      filters: [
        ...(formData.filters || []),
        { field: fields[0], operator: 'equals', value: '' }
      ]
    });
  };

  const removeFilter = (index: number) => {
    const updatedFilters = formData.filters?.filter((_, i) => i !== index) || [];
    setFormData({ ...formData, filters: updatedFilters });
  };

  const updateFilter = (index: number, updates: Partial<ReportFilter>) => {
    const updatedFilters = formData.filters?.map((f, i) => 
      i === index ? { ...f, ...updates } : f
    ) || [];
    setFormData({ ...formData, filters: updatedFilters });
  };

  const getChartIcon = (type: string) => {
    switch (type) {
      case 'bar': return <BarChart3 size={18} />;
      case 'pie': return <PieChart size={18} />;
      case 'line': return <LineChart size={18} />;
      case 'table': return <FileText size={18} />;
      default: return <BarChart3 size={18} />;
    }
  };

  const getDataSourceIcon = (source: string) => {
    switch (source) {
      case 'devices': return <Monitor size={16} />;
      case 'compliance': return <CheckCircle size={16} />;
      case 'applications': return <FileText size={16} />;
      case 'deployments': return <TrendingUp size={16} />;
      case 'policies': return <Server size={16} />;
      default: return <FileText size={16} />;
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-2">
            <div>
              <h1 className="text-3xl font-bold text-slate-900">Custom Reports</h1>
              <p className="text-slate-600 mt-1">Create and manage custom device analytics reports</p>
            </div>
            <button
              onClick={handleCreateReport}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors shadow-md"
            >
              <Plus size={18} />
              Create Report
            </button>
          </div>
        </div>

        {/* Reports Grid */}
        {reports.length === 0 ? (
          <div className="bg-white rounded-lg border-2 border-dashed border-slate-300 p-12 text-center">
            <FileText size={48} className="mx-auto text-slate-400 mb-4" />
            <h3 className="text-lg font-semibold text-slate-700 mb-2">No Custom Reports</h3>
            <p className="text-slate-500 mb-6">Create your first custom report to analyze device data</p>
            <button
              onClick={handleCreateReport}
              className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              <Plus size={18} />
              Create Report
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {reports.map((report) => (
              <div
                key={report.id}
                className="bg-white rounded-lg border border-slate-200 p-5 hover:shadow-lg transition-shadow"
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-blue-100 rounded-lg text-blue-600">
                      {getChartIcon(report.chartType)}
                    </div>
                    <div>
                      <h3 className="font-semibold text-slate-800">{report.name}</h3>
                      {report.description && (
                        <p className="text-xs text-slate-500 mt-1">{report.description}</p>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => handleEditReport(report)}
                      className="p-1.5 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors"
                      title="Edit"
                    >
                      <Edit size={16} />
                    </button>
                    <button
                      onClick={() => handleDeleteReport(report.id)}
                      className="p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                      title="Delete"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>

                <div className="flex items-center gap-4 text-xs text-slate-500 mb-4">
                  <div className="flex items-center gap-1">
                    {getDataSourceIcon(report.dataSource)}
                    <span className="capitalize">{report.dataSource}</span>
                  </div>
                  <div className="flex items-center gap-1">
                    {getChartIcon(report.chartType)}
                    <span className="capitalize">{report.chartType}</span>
                  </div>
                </div>

                {report.lastRun && (
                  <div className="text-xs text-slate-400 mb-4 flex items-center gap-1">
                    <Clock size={12} />
                    Last run: {new Date(report.lastRun).toLocaleDateString()}
                  </div>
                )}

                <div className="flex items-center gap-2 pt-4 border-t border-slate-100">
                  <button
                    onClick={() => handleRunReport(report)}
                    className="flex-1 flex items-center justify-center gap-2 px-3 py-2 bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200 transition-colors text-sm font-medium"
                  >
                    <Eye size={16} />
                    View
                  </button>
                  <button
                    onClick={() => handleExportReport(report, 'csv')}
                    className="p-2 text-slate-600 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                    title="Export"
                  >
                    <Download size={16} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Create/Edit Report Modal */}
        {(showCreateModal || showEditModal) && (
          <div className="fixed inset-0 bg-slate-900/60 z-50 flex items-center justify-center backdrop-blur-sm p-4">
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
              <div className="bg-slate-50 border-b border-slate-200 p-4 flex justify-between items-center shrink-0">
                <h3 className="font-semibold text-slate-800 flex items-center gap-2">
                  <FileText size={18} className="text-slate-500" />
                  {showEditModal ? 'Edit Report' : 'Create Custom Report'}
                </h3>
                <button
                  onClick={() => {
                    setShowCreateModal(false);
                    setShowEditModal(false);
                    setFormData({});
                    setSelectedReport(null);
                  }}
                  className="text-slate-400 hover:text-slate-600"
                >
                  <X size={20} />
                </button>
              </div>

              <div className="p-6 overflow-y-auto">
                <div className="space-y-4">
                  {/* Report Name */}
                  <div>
                    <label className="block text-sm font-semibold text-slate-600 mb-2">
                      Report Name *
                    </label>
                    <input
                      type="text"
                      value={formData.name || ''}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="w-full border border-slate-300 rounded-md p-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="e.g., Windows Device Compliance Report"
                    />
                  </div>

                  {/* Description */}
                  <div>
                    <label className="block text-sm font-semibold text-slate-600 mb-2">
                      Description
                    </label>
                    <textarea
                      value={formData.description || ''}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      className="w-full border border-slate-300 rounded-md p-2 h-20 resize-none focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Optional description of the report..."
                    />
                  </div>

                  {/* Data Source */}
                  <div>
                    <label className="block text-sm font-semibold text-slate-600 mb-2">
                      Data Source *
                    </label>
                    <select
                      value={formData.dataSource || 'devices'}
                      onChange={(e) => {
                        setFormData({
                          ...formData,
                          dataSource: e.target.value as any,
                          filters: [],
                          columns: []
                        });
                      }}
                      className="w-full border border-slate-300 rounded-md p-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="devices">Devices</option>
                      <option value="compliance">Compliance</option>
                      <option value="applications">Applications</option>
                      <option value="deployments">Deployments</option>
                      <option value="policies">Policies</option>
                    </select>
                  </div>

                  {/* Chart Type */}
                  <div>
                    <label className="block text-sm font-semibold text-slate-600 mb-2">
                      Chart Type *
                    </label>
                    <div className="grid grid-cols-4 gap-3">
                      {(['bar', 'pie', 'line', 'table'] as const).map((type) => (
                        <button
                          key={type}
                          onClick={() => setFormData({ ...formData, chartType: type })}
                          className={`flex flex-col items-center gap-2 p-3 border-2 rounded-lg transition-colors ${
                            formData.chartType === type
                              ? 'border-blue-600 bg-blue-50 text-blue-700'
                              : 'border-slate-200 hover:border-slate-300 text-slate-600'
                          }`}
                        >
                          {getChartIcon(type)}
                          <span className="text-xs font-medium capitalize">{type}</span>
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Filters */}
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <label className="block text-sm font-semibold text-slate-600">
                        Filters
                      </label>
                      <button
                        onClick={addFilter}
                        className="text-sm text-blue-600 hover:text-blue-700 flex items-center gap-1"
                      >
                        <Plus size={14} />
                        Add Filter
                      </button>
                    </div>
                    {formData.filters && formData.filters.length > 0 ? (
                      <div className="space-y-2">
                        {formData.filters.map((filter, index) => {
                          const fields = dataSourceFields[formData.dataSource || 'devices'] || [];
                          return (
                            <div key={index} className="flex items-center gap-2 p-3 bg-slate-50 rounded-lg border border-slate-200">
                              <select
                                value={filter.field}
                                onChange={(e) => updateFilter(index, { field: e.target.value })}
                                className="flex-1 border border-slate-300 rounded-md p-2 text-sm"
                              >
                                {fields.map((field) => (
                                  <option key={field} value={field}>
                                    {field.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                                  </option>
                                ))}
                              </select>
                              <select
                                value={filter.operator}
                                onChange={(e) => updateFilter(index, { operator: e.target.value as any })}
                                className="border border-slate-300 rounded-md p-2 text-sm"
                              >
                                <option value="equals">Equals</option>
                                <option value="contains">Contains</option>
                                <option value="greater_than">Greater Than</option>
                                <option value="less_than">Less Than</option>
                                <option value="between">Between</option>
                                <option value="in">In</option>
                              </select>
                              <input
                                type="text"
                                value={Array.isArray(filter.value) ? filter.value.join(',') : filter.value}
                                onChange={(e) => updateFilter(index, { value: e.target.value })}
                                className="flex-1 border border-slate-300 rounded-md p-2 text-sm"
                                placeholder="Value"
                              />
                              <button
                                onClick={() => removeFilter(index)}
                                className="p-2 text-red-600 hover:bg-red-50 rounded transition-colors"
                              >
                                <X size={16} />
                              </button>
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <p className="text-sm text-slate-500 italic">No filters added. Click "Add Filter" to add one.</p>
                    )}
                  </div>
                </div>
              </div>

              <div className="bg-slate-50 border-t border-slate-200 p-4 flex justify-end gap-2 shrink-0">
                <button
                  onClick={() => {
                    setShowCreateModal(false);
                    setShowEditModal(false);
                    setFormData({});
                    setSelectedReport(null);
                  }}
                  className="px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveReport}
                  className="px-4 py-2 text-sm font-medium bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
                >
                  <Save size={16} />
                  {showEditModal ? 'Update Report' : 'Create Report'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* View Report Modal */}
        {viewingReport && (
          <div className="fixed inset-0 bg-slate-900/60 z-50 flex items-center justify-center backdrop-blur-sm p-4">
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-5xl max-h-[90vh] overflow-hidden flex flex-col">
              <div className="bg-slate-50 border-b border-slate-200 p-4 flex justify-between items-center shrink-0">
                <h3 className="font-semibold text-slate-800 flex items-center gap-2">
                  {getChartIcon(viewingReport.chartType)}
                  {viewingReport.name}
                </h3>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => handleExportReport(viewingReport, 'csv')}
                    className="p-2 text-slate-600 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                    title="Export CSV"
                  >
                    <Download size={18} />
                  </button>
                  <button
                    onClick={() => setViewingReport(null)}
                    className="text-slate-400 hover:text-slate-600"
                  >
                    <X size={20} />
                  </button>
                </div>
              </div>

              <div className="p-6 overflow-y-auto">
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                  <p className="text-sm text-blue-800">
                    <strong>Note:</strong> This is a preview. In production, this would display actual data from the backend based on the selected filters and data source.
                  </p>
                </div>

                <div className="bg-slate-100 rounded-lg p-12 text-center">
                  <div className="text-slate-400 mb-4">
                    {getChartIcon(viewingReport.chartType)}
                  </div>
                  <p className="text-slate-600 font-medium">Report Preview</p>
                  <p className="text-sm text-slate-500 mt-2">
                    Data Source: <span className="font-medium capitalize">{viewingReport.dataSource}</span>
                  </p>
                  <p className="text-sm text-slate-500">
                    Chart Type: <span className="font-medium capitalize">{viewingReport.chartType}</span>
                  </p>
                  {viewingReport.filters.length > 0 && (
                    <p className="text-sm text-slate-500 mt-2">
                      Filters: {viewingReport.filters.length} applied
                    </p>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CustomReports;

